import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePromptStore } from '../../../store/usePrompt';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronLeft, 
  faSave, 
  faUndo,
  faPlus,
  faEdit,
  faCode,
  faQuestionCircle,
  faExclamationCircle,
  faCheckCircle,
  faCopy,
  faWandMagicSparkles,
  faChevronRight,
  faPlay,
  faRobot,
  faXmark,
  faSpinner,
  faClock,
  faClipboard
} from '@fortawesome/free-solid-svg-icons';
import PParamWrapper from './param/PParamWrapper';
import { testPromptWithOpenAI, testPromptWithBackend } from '../../../service/promptTestAPI';

/**
 * @author taulmax
 * @date 2024.10.17
 * @description 프롬프트 생성/수정 페이지
 */
const PromptCUD = ({ mode }: { mode: 'C' | 'U' }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { getPromptDetail, promptDetail, postPrompt, updatePrompt } =
    usePromptStore();

  const [promptTitle, setPromptTitle] = useState<string>('');
  const [promptCode, setPromptCode] = useState<string>('');
  const [prompt, setPrompt] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [hasChanges, setHasChanges] = useState<boolean>(false);
  
  // 테스트 관련 상태
  const [showTestPanel, setShowTestPanel] = useState<boolean>(false);
  const [showParamPanel, setShowParamPanel] = useState<boolean>(false);
  const [selectedModel, setSelectedModel] = useState<string>('gpt-4o-mini');
  const [testResult, setTestResult] = useState<string>('');
  const [isTestLoading, setIsTestLoading] = useState<boolean>(false);
  const [testVariables, setTestVariables] = useState<{[key: string]: string}>({});
  const [testHistory, setTestHistory] = useState<Array<{
    id: string;
    timestamp: Date;
    prompt: string;
    model: string;
    variables: {[key: string]: string};
    result: string;
  }>>([]);
  const [initialData, setInitialData] = useState({
    title: '',
    content: '',
  });
  
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);

  const checkForChanges = useCallback(() => {
    setHasChanges(
      promptTitle !== initialData.title || prompt !== initialData.content
    );
  }, [promptTitle, prompt, initialData]);
  
  const resetChanges = useCallback(() => {
    setPromptTitle(initialData.title);
    setPrompt(initialData.content);
    setHasChanges(false);
  }, [initialData.content, initialData.title]);

  const insertAtCursor = useCallback(
    (text: string) => {
      const textarea = textareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue = prompt.slice(0, start) + text + prompt.slice(end);
        setPrompt(newValue);

        setTimeout(() => {
          const newPosition = start + text.length;
          textarea.setSelectionRange(newPosition, newPosition);
          textarea.focus();
        }, 0);
      }
    },
    [prompt]
  );

  const handleCopyCode = () => {
    navigator.clipboard.writeText(promptCode);
    setShowCopyTooltip(true);
    setTimeout(() => setShowCopyTooltip(false), 2000);
  };

  const onClickSubmit = useCallback(() => {
    if (!promptTitle) {
      toast.error('프롬프트 제목을 입력해주세요.');
      return;
    }
    if (!promptCode) {
      toast.error('프롬프트 코드를 입력해주세요.');
      return;
    }
    if (!prompt) {
      toast.error('프롬프트를 입력해주세요.');
      return;
    }

    const promptData = {
      title: promptTitle,
      promptCode: promptCode.trim(),
      content: prompt,
    };

    if (mode === 'C') {
      postPrompt(promptData, navigate);
    }

    if (mode === 'U') {
      const id = location.pathname.replace('/prompt/', '');
      const updatePromptData: { [key: string]: string } = {};
      if (promptTitle !== initialData.title)
        updatePromptData.title = promptTitle;
      if (prompt !== initialData.content) updatePromptData.content = prompt;

      updatePrompt(id, updatePromptData);
      setInitialData(promptData);
      setHasChanges(false);
    }
  }, [
    initialData.content,
    initialData.title,
    location.pathname,
    mode,
    navigate,
    postPrompt,
    prompt,
    promptCode,
    promptTitle,
    updatePrompt,
  ]);

  useEffect(() => {
    checkForChanges();
  }, [promptTitle, promptCode, prompt, checkForChanges]);

  useEffect(() => {
    if (mode === 'U' && !promptDetail) {
      const id = location.pathname.replace('/prompt/', '');
      getPromptDetail(id);
    }
  }, [getPromptDetail, location.pathname, mode, promptDetail]);

  useEffect(() => {
    if (promptDetail) {
      setPromptTitle(promptDetail.title);
      setPromptCode(promptDetail.promptCode);
      setPrompt(promptDetail.content);
      setInitialData(promptDetail);
    }
  }, [promptDetail]);

  return (
    <div className="w-full h-full flex flex-col overflow-hidden bg-gray-100">
      {/* 헤더 */}
      <div className="flex justify-between items-center px-4 md:px-6 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-3 md:gap-4">
          <button
            onClick={() => navigate('/prompt')}
            className="text-gray-600 hover:text-gray-900"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <h1 className="text-lg md:text-xl font-bold text-gray-900 truncate">
            {mode === 'C' ? '프롬프트 등록' : '프롬프트 수정'}
          </h1>
        </div>
        
        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden md:flex items-center gap-3">
            {mode === 'U' && hasChanges && (
              <span className="text-sm text-orange-600 flex items-center gap-1">
                <FontAwesomeIcon icon={faExclamationCircle} />
                변경사항
              </span>
            )}
            {mode === 'U' && !hasChanges && (
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
                저장됨
              </div>
            )}
          </div>
          <div className="lg:hidden flex items-center gap-2 ml-2">
            <button onClick={() => setShowParamPanel(true)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-full">
              <FontAwesomeIcon icon={faWandMagicSparkles} />
            </button>
            <button onClick={() => setShowTestPanel(true)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-full">
              <FontAwesomeIcon icon={faPlay} />
            </button>
          </div>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-y-auto lg:overflow-hidden">
        {/* 왼쪽 메인 영역 */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* 상단 입력 필드들 */}
          <div className="px-4 md:px-6 py-3 border-b border-gray-200 bg-white">
            <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
              <div className="flex-1">
                <input
                  type="text"
                  value={promptTitle}
                  onChange={(e) => setPromptTitle(e.target.value)}
                  placeholder="프롬프트 제목을 입력하세요"
                  className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-all duration-200"
                />
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative">
                  <input
                    type="text"
                    value={promptCode}
                    onChange={(e) => setPromptCode(e.target.value)}
                    placeholder="PROMPT_CODE"
                    disabled={mode === 'U'}
                    className={`w-full sm:w-44 px-4 py-2 font-mono text-sm rounded-md border ${mode === 'U' ? 'bg-gray-100 border-gray-300 text-gray-600' : 'bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200'} transition-all duration-200`}
                  />
                  {mode === 'U' && (
                    <button onClick={handleCopyCode} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <FontAwesomeIcon icon={faCopy} className="text-sm" />
                    </button>
                  )}
                  {showCopyTooltip && (
                    <div className="absolute right-0 -top-8 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg">
                      복사됨!
                    </div>
                  )}
                </div>
                
                {(mode === 'C' || (mode === 'U' && hasChanges)) && (
                  <div className="flex items-center gap-2">
                    {mode === 'U' && hasChanges && (
                      <button onClick={resetChanges} className="flex-1 sm:flex-none px-4 py-2 text-sm bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center gap-2">
                        <FontAwesomeIcon icon={faUndo} className="text-sm" />
                        초기화
                      </button>
                    )}
                    <button onClick={onClickSubmit} className="flex-1 sm:flex-none px-5 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-md transition-colors duration-200 flex items-center justify-center gap-2">
                      <FontAwesomeIcon icon={faSave} className="text-sm" />
                      {mode === 'C' ? '등록' : '저장'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Textarea 영역 */}
          <div className="flex-1 flex flex-col bg-white overflow-hidden">
            <div className="px-4 md:px-6 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-2 md:gap-4">
                <span className="text-sm font-semibold text-gray-700">프롬프트 내용</span>
                <span className="text-xs text-gray-600 font-mono bg-white border border-gray-200 px-2 py-1 rounded">
                  {prompt.length.toLocaleString()} characters
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 hidden lg:inline">
                  파라미터를 삽입하려면 오른쪽 패널을 사용하세요
                </span>
                <button
                  onClick={() => setShowTestPanel(!showTestPanel)}
                  className={`hidden lg:flex px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 items-center gap-1.5 ${showTestPanel ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  <FontAwesomeIcon icon={faPlay} className="text-[10px]" />
                  테스트
                </button>
              </div>
            </div>
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  // Ctrl+S 또는 Cmd+S 감지
                  if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                    e.preventDefault(); // 브라우저 기본 저장 동작 방지
                    if (mode === 'U' && hasChanges) {
                      onClickSubmit(); // 저장 함수 호출
                    }
                  }
                }}
                placeholder="프롬프트 내용을 입력하세요...\n\n예시:\n당신은 전문 상담사입니다. ${name}님의 요청사항을 다음과 같이 처리해주세요..."
                className="absolute inset-0 w-full h-full px-4 md:px-6 py-4 bg-white text-gray-800 font-mono text-sm leading-relaxed resize-none focus:outline-none overflow-y-auto border-0"
                style={{ tabSize: 2 }}
              />
            </div>
          </div>
        </div>
        
        {/* 테스트 패널 (모바일: 오버레이, 데스크탑: 사이드) */}
        {showTestPanel && (
          <div className="fixed inset-0 z-20 bg-gray-50 flex flex-col overflow-hidden lg:static lg:z-auto lg:flex-1 lg:min-w-[400px] lg:max-w-[600px] lg:border-l-2 border-gray-200">
            {/* 테스트 헤더 */}
            <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faRobot} className="text-blue-600 text-sm" />
                <h3 className="text-sm font-semibold text-gray-700">프롬프트 테스트</h3>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                >
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  <option value="gpt-4">GPT-4</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo</option>
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="gpt-4o-mini">GPT-4o mini</option>
                </select>
              </div>
              <button
                onClick={() => setShowTestPanel(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                <FontAwesomeIcon icon={faXmark} className="text-sm" />
              </button>
            </div>
            
            {/* 테스트 콘텐츠 */}
            <div className="flex-1 flex flex-col">
              {/* 파라미터 입력 영역 */}
              {(() => {
                // 프롬프트에서 ${변수명} 형태의 변수 추출
                const variableMatches = prompt.match(/\$\{([^}]+)\}/g) || [];
                const variableNames = variableMatches.map(match => match.slice(2, -1));
                const variables = Array.from(new Set(variableNames));
                
                return variables.length > 0 ? (
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">변수 값 입력</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {variables.map(variable => (
                        <div key={variable} className="flex items-center gap-2">
                          <label className="text-xs text-gray-600 min-w-[80px] flex-shrink-0">
                            ${variable}:
                          </label>
                          <input
                            type="text"
                            value={testVariables[variable] || ''}
                            onChange={(e) => setTestVariables(prev => ({
                              ...prev,
                              [variable]: e.target.value
                            }))}
                            placeholder={`${variable} 값 입력`}
                            className="flex-1 px-2 py-1 text-xs bg-white border border-gray-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
              
              {/* 테스트 실행 영역 */}
              <div className="px-4 py-3 border-b border-gray-200">
                <button
                  onClick={async () => {
                    setIsTestLoading(true);
                    try {
                      // OpenAI API 직접 호출
                      const response = await testPromptWithOpenAI({
                        prompt,
                        model: selectedModel,
                        variables: testVariables
                      });
                      
                      setTestResult(response.result);
                      
                      // 변수 치환된 프롬프트 생성 (히스토리용)
                      let processedPrompt = prompt;
                      Object.entries(testVariables).forEach(([key, value]) => {
                        processedPrompt = processedPrompt.replace(new RegExp(`\\$\\{${key}\\}`, 'g'), value);
                      });
                      
                      // 히스토리에 추가
                      const historyItem = {
                        id: Date.now().toString(),
                        timestamp: new Date(),
                        prompt: processedPrompt,
                        model: selectedModel,
                        variables: testVariables,
                        result: response.result
                      };
                      setTestHistory(prev => [historyItem, ...prev].slice(0, 10)); // 최근 10개만
                    } catch (error) {
                      toast.error('프롬프트 테스트 중 오류가 발생했습니다.');
                      console.error(error);
                    } finally {
                      setIsTestLoading(false);
                    }
                  }}
                  disabled={!prompt || isTestLoading}
                  className="w-full px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white font-medium rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  {isTestLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      테스트 중...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlay} />
                      프롬프트 테스트 실행
                    </>
                  )}
                </button>
              </div>
              
              {/* 히스토리 탭 */}
              {testHistory.length > 0 && (
                <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-medium text-gray-700">테스트 히스토리</h4>
                    <button
                      onClick={() => setTestHistory([])}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      전체 삭제
                    </button>
                  </div>
                </div>
              )}
              
              {/* 결과 표시 영역 */}
              <div className="flex-1 overflow-y-auto p-4">
                {testResult ? (
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faRobot} className="text-blue-600 text-sm" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-gray-500">{selectedModel}</p>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(testResult);
                              toast.success('결과가 복사되었습니다.');
                            }}
                            className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                          >
                            <FontAwesomeIcon icon={faClipboard} />
                            복사
                          </button>
                        </div>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{testResult}</p>
                      </div>
                    </div>
                  </div>
                ) : testHistory.length > 0 ? (
                  <div className="space-y-3">
                    {testHistory.map((item) => (
                      <div 
                        key={item.id}
                        className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 cursor-pointer hover:border-blue-300 transition-colors"
                        onClick={() => {
                          setTestResult(item.result);
                          setTestVariables(item.variables);
                          setSelectedModel(item.model);
                        }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faClock} className="text-gray-400 text-xs" />
                            <span className="text-xs text-gray-500">
                              {new Date(item.timestamp).toLocaleString('ko-KR')}
                            </span>
                          </div>
                          <span className="text-xs text-gray-600 font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                            {item.model}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {item.result}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <FontAwesomeIcon icon={faRobot} className="text-4xl mb-3" />
                    <p className="text-sm">테스트를 실행하여 결과를 확인하세요</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 파라미터 패널 (모바일: 오버레이, 데스크탑: 사이드) */}
        <div className={`${showParamPanel ? 'flex' : 'hidden'} lg:flex fixed inset-0 z-30 bg-gray-50 flex-col overflow-hidden lg:static lg:z-auto lg:w-80 lg:border-l-2 border-gray-200`}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faWandMagicSparkles} className="text-purple-600 text-sm" />
              <h2 className="text-sm font-semibold text-gray-700">파라미터 도구</h2>
            </div>
            <button onClick={() => setShowParamPanel(false)} className="lg:hidden p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <PParamWrapper insertAtCursor={insertAtCursor} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptCUD;